import { Split } from 'lucide-react';
import type { BoolExpr, LBNodeData } from '@/proto/logiblocks/logiblocks';
import { LogicalOp } from '@/proto/logiblocks/logiblocks';
import {
  formatVariableRef,
  formatVariableValue,
} from '@/pages/Logiblocks/variables/variableUtils';
import {
  formatUtcSecondsAsUserTime,
  getDayOfWeekShortLabel,
  utcSecondsToUserSeconds,
} from '../../dateTimeUtils';
import { getUserTimeZone } from '@/common/utils/utils';
import { CONTROL_FLOW_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import { expressionOpLabel, getExprLhs, getExprOp } from './expressionOp';
import { LOGICAL_OP_LABELS } from '../../enumData';
import { ConditionalCustomization } from './ConditionalCustomization';
import { defaultNodeData } from '../../graphUtils';
import {
  validateGroupRefs,
  validateGroupStructure,
  validateOperatorCatalog,
} from './conditionalNodeUtils';
import { formatDateTimeSetExprSubtitle } from './CustomDateTimeDayPicker/dateSetUtils';

// Find the first leaf expression, recursing into nested groups.
function findFirstLeaf(expr: BoolExpr): BoolExpr | undefined {
  if (!expr.group) {
    return expr;
  }
  const first = expr.group.expressions[0];
  return first ? findFirstLeaf(first) : undefined;
}

// Format a leaf expression for the subtitle, e.g. "status Equals "delivered""
// or "Current Time Within 9:00 AM – 5:00 PM".
function formatLeaf(expr: BoolExpr, graph: GraphData): string {
  const lhs = getExprLhs(expr);
  const op = getExprOp(expr);
  const lhsLabel = lhs ? formatVariableRef(lhs, graph.nodes, 'short') : '?';
  const opLabel = op ? expressionOpLabel(op) : '?';

  if (expr.recurring_window_expr) {
    const rw = expr.recurring_window_expr;
    const hasTime = rw.start_seconds != null || rw.end_seconds != null;
    if (!hasTime) {
      const days = rw.days_of_week
        .map((d) => getDayOfWeekShortLabel(d))
        .join(', ');
      return `${lhsLabel} ${opLabel} ${days}`;
    }
    const start = formatUtcSecondsAsUserTime(rw.start_seconds ?? 0);
    const end = formatUtcSecondsAsUserTime(rw.end_seconds ?? 0);
    return `${lhsLabel} ${opLabel} ${start} – ${end}`;
  }

  if (expr.date_time_set_expr) {
    return `${lhsLabel} ${opLabel} ${formatDateTimeSetExprSubtitle(expr.date_time_set_expr)}`;
  }

  if (expr.comparison_expr?.rhs) {
    const rhs = expr.comparison_expr.rhs;
    if (rhs.literal) {
      return `${lhsLabel} ${opLabel} ${formatVariableValue(rhs.literal)}`;
    }
    if (rhs.variable) {
      return `${lhsLabel} ${opLabel} ${formatVariableRef(rhs.variable, graph.nodes, 'short')}`;
    }
  }

  return `${lhsLabel} ${opLabel}`;
}

export const ConditionalNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'conditional',

  category: NodeCategory.CONTROL_FLOW,

  label: 'Path',

  hasInput: true,

  getIcon: (size: number) => (
    <Split size={size} color={CONTROL_FLOW_ICON_COLOR} />
  ),

  outputType: OutputType.SINGLE,

  getSubtitle: (data: LBNodeData, graph: GraphData) => {
    const root = data.conditional?.root;
    if (!root || root.expressions.length === 0) {
      return 'Always passes';
    }

    const { errors } = validateGroupStructure(root, 0, true);
    if (errors.length > 0) {
      return '(error!)';
    }

    const first = findFirstLeaf(root.expressions[0]);
    if (first == null) {
      return '…';
    }

    const label = formatLeaf(first, graph);
    if (root.expressions.length === 1) {
      const rw = first.recurring_window_expr;
      const tz = getUserTimeZone().name;
      const isOvernightLocally =
        rw &&
        (rw.start_seconds != null || rw.end_seconds != null) &&
        utcSecondsToUserSeconds(rw.end_seconds ?? 0, tz) <
          utcSecondsToUserSeconds(rw.start_seconds ?? 0, tz);
      if (isOvernightLocally) {
        const lhs = getExprLhs(first);
        const op = getExprOp(first);
        const lhsLabel = lhs
          ? formatVariableRef(lhs, graph.nodes, 'short')
          : '?';
        const opLabel = op ? expressionOpLabel(op) : '?';
        const start = formatUtcSecondsAsUserTime(rw.start_seconds ?? 0);
        const end = formatUtcSecondsAsUserTime(rw.end_seconds ?? 0);
        return (
          <>
            {lhsLabel} {opLabel} {start} –{' '}
            <span style={{ whiteSpace: 'nowrap' }}>
              {end}
              <sup style={{ color: 'red', marginLeft: 1 }}>+1</sup>
            </span>
          </>
        );
      }
      return label;
    }
    const joinOp = LOGICAL_OP_LABELS[root.operators[0] ?? LogicalOp.AND];
    return `${label} ${joinOp} …`;
  },

  getValidationErrors: (
    nodeId: string,
    nodeData: LBNodeData,
    graph: GraphData
  ) => {
    const root = nodeData.conditional?.root;
    if (!root) {
      return undefined;
    }
    const { errors: structErrors } = validateGroupStructure(root, 0, true);
    const refErrors = validateGroupRefs(root, 0, nodeId, graph).errors;

    // Operator-vs-type validation runs on every conditional. Each leaf's type
    // is resolved from its variable ref, which points at the ancestor node
    // that produces the variable; leaves whose type can't be resolved are
    // skipped, mirroring the backend operator-catalog check.
    const { errors: operatorErrors } = validateOperatorCatalog(root, 0, graph);

    const errors = [...structErrors, ...refErrors, ...operatorErrors];
    return errors.length > 0 ? errors : undefined;
  },

  CustomizationComponent: ConditionalCustomization,

  createDefaultNodeData: () =>
    defaultNodeData('conditional', {
      root: { expressions: [], operators: [] },
    }),
};
